import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearch } from '@tanstack/react-router';
import type { ColumnDef, PaginationState } from '@tanstack/react-table';
import { CalendarRange, Headphones, Zap } from 'lucide-react';
import { describeWindow, type QueueLane } from '@vagusx/shared';

import type { ContactPolicy, OutreachCampaign, QueueRow } from '@vagusx/clients';
import { TablePage } from '@/components/table-page';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { useContactPolicy } from '@/hooks/use-outreach';
import {
  useExcludePatient,
  useQueueLane,
  useQueueSummary,
  useRunNow,
} from '@/hooks/use-outreach-queue';
import { cn } from '@/lib/utils';
import { PatientSheet } from '@/pages/patients/PatientSheet';
import { ReferralSheet } from '@/pages/referrals/ReferralSheet';
import { RankedProvidersCell, RankedTypeCell } from '@/pages/referrals/referral-cells';
import { LANES, REASON_LABELS, initialLane, laneHint } from './labels';
import { ReadySlotsSheet } from './ReadySlotsSheet';

/**
 * A campaign's queue (ADR-318, H): where everyone it qualifies is, lane by
 * lane — Ready (no run yet; ADR-341), Waiting, Not ready, Calling now, Needs a
 * person and Done, with Callback set and On hold set apart as detours.
 * Patient names are PHI, shown under `outreach:view`, never logged.
 *
 * A row's patient and referral open in slide-overs over the queue — the
 * patient page and the referral panel, as they are elsewhere — so staff can
 * see what is missing without leaving the campaign. Which one is open lives in
 * the URL (`?patient=`, `?referral=`), so Back closes it.
 */
export function QueueTab({
  campaign,
  canRun,
  active = true,
}: {
  campaign: OutreachCampaign;
  /** Call now and Take out need outreach:run; without it they are not shown. */
  canRun: boolean;
  /** The tab is showing; nothing is fetched or polled otherwise. */
  active?: boolean;
}): React.ReactElement {
  const [lane, setLane] = useState<QueueLane>(initialLane(campaign.status));
  const [paging, setPaging] = useState<PaginationState>({ pageIndex: 0, pageSize: 50 });
  const [takingOut, setTakingOut] = useState<QueueRow | null>(null);
  const summary = useQueueSummary(campaign.id, lane, { enabled: active });
  const rows = useQueueLane(campaign.id, lane, { enabled: active });
  const { data: policy } = useContactPolicy();
  const runNow = useRunNow(campaign.id);
  const search = useSearch({ from: '/_authenticated/campaigns/$id/' });
  const navigate = useNavigate();
  // Opening is a push, so Back closes the slide-over rather than leaving the
  // campaign; closing replaces, so Back after closing does not reopen it. One
  // at a time: opening either closes the other, and the call that was open
  // over a referral goes with it.
  const show = useCallback(
    (sheet: { referral?: string; patient?: string; slots?: string }, replace = false): void =>
      void navigate({
        to: '.',
        search: (prev) => ({
          ...prev,
          referral: undefined,
          patient: undefined,
          slots: undefined,
          call: undefined,
          ...sheet,
        }),
        replace,
      }),
    [navigate]
  );
  const closeSheets = (): void => show({}, true);
  // The mutation's function and flag are stable; the object around them is not.
  const { mutate: runNowMutate, isPending: runNowBusy } = runNow;

  const data = useMemo(() => rows.data?.pages.flatMap((p) => p.rows) ?? [], [rows.data]);
  const view = pageOfLoaded(data, rows.hasNextPage, paging);
  // The table's own pager drives the server's: a page past what is loaded
  // fetches the next server page. A failed fetch is shown, not retried in a loop.
  const { fetchNextPage, isFetchingNextPage, isFetchNextPageError } = rows;
  useEffect(() => {
    if (view.needMore && !isFetchingNextPage && !isFetchNextPageError) void fetchNextPage();
  }, [view.needMore, isFetchingNextPage, isFetchNextPageError, fetchNextPage]);
  const selectLane = (next: QueueLane): void => {
    setLane(next);
    setPaging((p) => ({ ...p, pageIndex: 0 }));
  };
  const counts = laneCounts(summary.data?.lanes, lane, rows.data ? data : null, rows.hasNextPage);
  const current = LANES.find((l) => l.lane === lane)!;
  const columns = useMemo(
    () =>
      queueColumns({
        lane,
        canRun,
        onRunNow: (row) => row.runId && runNowMutate(row.runId),
        onTakeOut: setTakingOut,
        onOpenPatient: (row) => show({ patient: row.patient.id }),
        onOpenReferral: (taskId) => show({ referral: taskId }),
        onOpenSlots: (taskId) => show({ slots: taskId }),
        busy: runNowBusy,
      }),
    [lane, canRun, show, runNowMutate, runNowBusy]
  );

  return (
    <>
      <TablePage
        title={current.label}
        description={laneHint(current.lane, campaign.status, policy?.enabled ?? true)}
        columns={columns}
        data={view.rows}
        total={counts.get(lane)}
        isLoading={rows.isLoading || (view.needMore && !isFetchNextPageError)}
        pagination={{ pageIndex: view.pageIndex, pageSize: paging.pageSize }}
        onPaginationChange={setPaging}
        error={rows.error}
        emptyMessage="Nobody here right now."
        errorMessage="Could not load this part of the queue."
        aboveTable={
          <div className="space-y-3">
            <StatusStrip campaign={campaign} policy={policy} checkedAt={summary.data?.checkedAt} />
            <LaneChips lane={lane} counts={counts} onSelect={selectLane} />
          </div>
        }
      />

      <TakeOutDialog campaignId={campaign.id} row={takingOut} onClose={() => setTakingOut(null)} />
      {/* Every campaign tab stays mounted and these portal to the page, so
          they open only while the queue is the tab showing. */}
      <ReferralSheet taskId={active ? search.referral : undefined} onClose={closeSheets} />
      <PatientSheet patientId={active ? search.patient : undefined} onClose={closeSheets} />
      <ReadySlotsSheet
        taskId={active ? search.slots : undefined}
        // The row's name when it is loaded; the sheet reads it itself otherwise.
        patientName={
          data.find((r) => r.subject?.taskId === search.slots)?.patient.name || undefined
        }
        onClose={closeSheets}
      />
    </>
  );
}

/**
 * The count per lane. The Calling lane polls live and the counts do not, so
 * once every page of it is loaded its own rows are its count: chip, title and
 * table agree about who is on a call.
 */
export function laneCounts(
  summary: ReadonlyArray<{ lane: QueueLane; count: number }> | undefined,
  lane: QueueLane,
  loadedRows: readonly QueueRow[] | null,
  morePages: boolean
): Map<QueueLane, number> {
  const counts = new Map((summary ?? []).map((l) => [l.lane, l.count]));
  if (lane === 'calling' && loadedRows && !morePages) counts.set('calling', loadedRows.length);
  return counts;
}

/**
 * One table page out of the rows loaded so far. The server pages by cursor, so
 * a page reaching past what is loaded needs more (`needMore`); with nothing
 * more to load, a page past the end (the lane shrank) falls back to the last.
 */
export function pageOfLoaded<T>(
  loaded: readonly T[],
  morePages: boolean,
  { pageIndex, pageSize }: PaginationState
): { rows: T[]; pageIndex: number; needMore: boolean } {
  const lastIndex = Math.max(0, Math.ceil(loaded.length / pageSize) - 1);
  const index = morePages ? pageIndex : Math.min(pageIndex, lastIndex);
  const start = index * pageSize;
  return {
    rows: loaded.slice(start, start + pageSize),
    pageIndex: index,
    needMore: morePages && loaded.length < start + pageSize,
  };
}

/**
 * Calling or paused, the calling hours, the practice's daily limit, and when
 * membership was last worked out. The API has no count of today's calls yet,
 * so the limit is stated on its own rather than as "N of M".
 */
export function StatusStrip({
  campaign,
  policy,
  checkedAt,
}: {
  campaign: OutreachCampaign;
  policy: ContactPolicy | undefined;
  checkedAt: string | null | undefined;
}): React.ReactElement {
  const calling = campaign.status === 'active' && (policy?.enabled ?? true);
  return (
    <div className="flex flex-wrap items-center gap-x-5 gap-y-1 rounded-md border px-4 py-2.5 text-sm">
      <span className="flex items-center gap-2 font-medium">
        <span
          className={cn('h-2 w-2 rounded-full', calling ? 'bg-primary' : 'bg-muted-foreground')}
        />
        {campaign.status !== 'active'
          ? campaign.status === 'paused'
            ? 'Paused'
            : 'Not started'
          : policy && !policy.enabled
            ? 'Automated calling is off for the practice'
            : 'Calling'}
      </span>
      {campaign.spec && (
        <span className="text-muted-foreground">
          Calling hours{' '}
          <span className="text-foreground">{describeWindow(campaign.spec.window)}</span>
        </span>
      )}
      {policy && (
        <span className="text-muted-foreground">
          Up to <span className="text-foreground tabular-nums">{policy.dailyCallCap}</span> calls a
          day
        </span>
      )}
      <span className="text-xs text-muted-foreground sm:ml-auto">
        {/* `undefined` is still loading (or not loaded); only `null` means the check failed. */}
        {checkedAt
          ? `Membership last checked ${new Date(checkedAt).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}`
          : checkedAt === null
            ? 'Membership could not be checked'
            : null}
      </span>
    </div>
  );
}

/** The lanes as chips with their counts, the main flow first and the detours set apart. */
export function LaneChips({
  lane,
  counts,
  onSelect,
}: {
  lane: QueueLane;
  counts: ReadonlyMap<QueueLane, number>;
  onSelect: (lane: QueueLane) => void;
}): React.ReactElement {
  const chip = (entry: (typeof LANES)[number]): React.ReactElement => {
    const count = counts.get(entry.lane);
    return (
      <button
        key={entry.lane}
        type="button"
        aria-pressed={entry.lane === lane}
        data-lane={entry.lane}
        onClick={() => onSelect(entry.lane)}
        className={cn(
          'flex min-w-24 flex-col items-start rounded-md border px-3 py-1.5 text-left transition-colors',
          entry.aside && 'border-dashed',
          entry.lane === lane ? 'border-primary bg-primary/5' : 'hover:bg-muted'
        )}
      >
        <span className="text-lg font-semibold tabular-nums">
          {count === undefined ? '—' : count.toLocaleString()}
        </span>
        <span className="text-xs text-muted-foreground">{entry.label}</span>
      </button>
    );
  };
  return (
    <div className="flex flex-wrap items-stretch gap-2" role="group" aria-label="Queue lanes">
      {LANES.filter((l) => !l.aside).map(chip)}
      <span className="mx-1 w-px self-stretch bg-border" aria-hidden />
      {LANES.filter((l) => l.aside).map(chip)}
    </div>
  );
}

const when = (iso: string | null): string =>
  iso
    ? new Date(iso).toLocaleString([], {
        weekday: 'short',
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
      })
    : '—';

/** Columns for one lane; actions only where they mean something, and only with outreach:run. */
export function queueColumns({
  lane,
  canRun,
  onRunNow,
  onTakeOut,
  onOpenPatient,
  onOpenReferral,
  onOpenSlots = () => {},
  busy,
}: {
  lane: QueueLane;
  canRun: boolean;
  onRunNow: (row: QueueRow) => void;
  onTakeOut: (row: QueueRow) => void;
  onOpenPatient: (row: QueueRow) => void;
  /** Opens the referral panel on the referral's task. */
  onOpenReferral: (taskId: string) => void;
  /** Opens the slots panel (patient and offered times) on the referral's task. */
  onOpenSlots?: (taskId: string) => void;
  busy: boolean;
}): ColumnDef<QueueRow>[] {
  const ended = lane === 'needs_person' || lane === 'done';
  // Ready and Not ready have no run: no step yet, and only Not ready has a reason.
  const unstarted = lane === 'ready' || lane === 'not_ready';
  return [
    {
      id: 'patient',
      header: 'Patient',
      cell: ({ row }) => (
        <Button
          variant="link"
          className="h-auto p-0 font-medium text-foreground"
          onClick={() => onOpenPatient(row.original)}
        >
          {row.original.patient.name}
        </Button>
      ),
    },
    {
      id: 'subject',
      header: 'Referral',
      cell: ({ row }) => {
        const subject = row.original.subject;
        if (!subject) return <span className="text-muted-foreground">—</span>;
        if (subject.kind !== 'referral') {
          return <span className="text-sm text-muted-foreground">{subject.label}</span>;
        }
        const taskId = subject.taskId;
        // No task points at the referral: there is no panel to open, only the document.
        return taskId ? (
          <Button variant="link" className="h-auto p-0" onClick={() => onOpenReferral(taskId)}>
            Open referral
          </Button>
        ) : (
          <Button variant="link" className="h-auto p-0" asChild>
            <Link to="/documents/$id" params={{ id: subject.id }}>
              Open referral
            </Link>
          </Button>
        );
      },
    },
    // The Ready lane shows each referral's type and provider pool the way the
    // Referrals page does. The pool comes on the row: the one readiness judged
    // the referral on, so the lane needs no second ranking request.
    ...(lane === 'ready'
      ? [
          {
            id: 'appointmentType',
            header: 'Appointment type',
            cell: ({ row }: { row: { original: QueueRow } }) => {
              const taskId = referralTaskOf(row.original);
              return taskId ? (
                <RankedTypeCell ranking={row.original.ranking} isLoading={false} />
              ) : (
                <span className="text-muted-foreground">—</span>
              );
            },
          } satisfies ColumnDef<QueueRow>,
          {
            id: 'providers',
            header: 'Provider(s)',
            cell: ({ row }: { row: { original: QueueRow } }) => {
              const taskId = referralTaskOf(row.original);
              return taskId ? (
                <RankedProvidersCell
                  taskId={taskId}
                  ranking={row.original.ranking}
                  isLoading={false}
                />
              ) : (
                <span className="text-muted-foreground">—</span>
              );
            },
          } satisfies ColumnDef<QueueRow>,
        ]
      : []),
    ...(unstarted
      ? []
      : [
          {
            id: 'step',
            header: 'Where',
            cell: ({ row }: { row: { original: QueueRow } }) => {
              const step = row.original.step;
              return step ? (
                <span className="text-sm">
                  {step.label}
                  {step.of > 1 && (
                    <span className="text-muted-foreground">
                      {' '}
                      · try {step.run} of {step.of}
                    </span>
                  )}
                </span>
              ) : (
                <span className="text-muted-foreground">—</span>
              );
            },
          } satisfies ColumnDef<QueueRow>,
        ]),
    ...(lane === 'waiting' || lane === 'callback' || lane === 'hold'
      ? [
          {
            id: 'next',
            header: 'Next',
            cell: ({ row }: { row: { original: QueueRow } }) => (
              <span className="text-sm tabular-nums">{when(row.original.nextAt)}</span>
            ),
          } satisfies ColumnDef<QueueRow>,
        ]
      : []),
    ...(lane === 'ready'
      ? []
      : [
          {
            id: 'reason',
            header: lane === 'not_ready' ? 'Missing' : ended ? 'How it ended' : 'Why',
            cell: ({ row }: { row: { original: QueueRow } }) =>
              row.original.reason ? (
                <span className="text-sm text-muted-foreground">
                  {REASON_LABELS[row.original.reason.code]}
                </span>
              ) : (
                <span className="text-muted-foreground">—</span>
              ),
          } satisfies ColumnDef<QueueRow>,
        ]),
    {
      id: 'actions',
      header: '',
      cell: ({ row }) => (
        <div className="flex justify-end gap-1">
          {lane === 'calling' && row.original.interactionId && (
            <Button variant="outline" size="sm" asChild>
              <Link to="/calls/live" search={{ call: row.original.interactionId }}>
                <Headphones className="mr-1.5 h-3.5 w-3.5" />
                Listen
              </Link>
            </Button>
          )}
          {lane === 'ready' && referralTaskOf(row.original) && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => onOpenSlots(referralTaskOf(row.original)!)}
            >
              <CalendarRange className="mr-1.5 h-3.5 w-3.5" />
              Slots
            </Button>
          )}
          {/* Only a waiting run can be brought forward (run-now refuses the rest). */}
          {canRun && lane === 'waiting' && row.original.runId && (
            <Button
              variant="outline"
              size="sm"
              disabled={busy}
              onClick={() => onRunNow(row.original)}
            >
              <Zap className="mr-1.5 h-3.5 w-3.5" />
              Call now
            </Button>
          )}
          {canRun && !ended && (
            <Button variant="ghost" size="sm" onClick={() => onTakeOut(row.original)}>
              Take out
            </Button>
          )}
        </div>
      ),
    },
  ];
}

/** The referral's task a row is about, or null for any other subject. */
function referralTaskOf(row: QueueRow): string | null {
  return row.subject?.kind === 'referral' ? row.subject.taskId : null;
}

/** Take a patient out of this campaign, with the reason staff give (R5). */
function TakeOutDialog({
  campaignId,
  row,
  onClose,
}: {
  campaignId: string;
  row: QueueRow | null;
  onClose: () => void;
}): React.ReactElement {
  const exclude = useExcludePatient(campaignId);
  const [reason, setReason] = useState('');
  const trimmed = reason.trim();
  // Every way out clears the reason, so it is never offered for the next patient.
  const close = (): void => {
    setReason('');
    onClose();
  };

  return (
    <Dialog
      open={row !== null}
      onOpenChange={(open) => {
        if (!open) close();
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Take {row?.patient.name} out of this campaign?</DialogTitle>
          <DialogDescription>
            This campaign stops for them at once and will not call them again. Other campaigns are
            not affected.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-1.5">
          <Label htmlFor="take-out-reason">Why</Label>
          <Textarea
            id="take-out-reason"
            value={reason}
            maxLength={500}
            onChange={(e) => setReason(e.target.value)}
          />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={close}>
            Cancel
          </Button>
          <Button
            disabled={!trimmed || exclude.isPending || !row}
            onClick={() =>
              row &&
              exclude.mutate(
                { patientId: row.patient.id, reason: trimmed },
                {
                  onSuccess: close,
                }
              )
            }
          >
            Take out
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
